import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Dropdown } from 'primereact/dropdown'
import { Message } from 'primereact/message'
import LtsInputText from '../lottus/lts-input-text'
import LtsInputNumber from '../lottus/lts-input-number'
import { resolverDatas, salvar, visualizar } from '../../services/api-agendamento-relatorio'
import {
  abrirAbaEmBranco,
  fecharAba,
  formatarDataBr,
  formatarDataIso,
  isFormula,
  lerFiltros,
  linhasIniciais,
  mensagemDeErro,
  montarFiltros,
  montarFormula,
  mostrarNaAba,
} from './agendamentoRelatorio.utils'
import { textoAgendamento, textosAgendamento } from './textos'
import './AgendarRelatorioDialog.scss'

const ESPERA_PREVIEW_MS = 400

/**
 * Dialogo de agendamento de relatorio (PHOENIX-106), comum as telas de relatorio fixas (DRE...) e dinamicas.
 * Nao conhece o relatorio: a tela informa quem ele e, quais campos sao datas e os filtros atuais.
 *
 * Props:
 * - visible, onHide
 * - relatorio: { path, oidDocumento, descricao } - path da tela fixa ('dre') ou documento da tela dinamica
 * - camposData: [{ campo: 'dataInicial', label: 'Data inicial' }] - uma linha da grade por campo; [] = sem grade
 * - filtrosTela: filtros atuais da tela (datas como Date ou yyyy-MM-dd); na edicao, os filtros salvos
 * - agendamento: o DTO salvo, na edicao (opcional)
 * - EditorFiltros: componente que edita os filtros que nao sao data, recebe { filtros, onChange(campo, valor) }
 *   (opcional; ver relatoriosAgendaveis)
 * - onSaved(agendamento): depois de salvar
 */
export const AgendarRelatorioDialog = ({
  visible,
  onHide,
  relatorio,
  camposData,
  filtrosTela,
  agendamento,
  EditorFiltros,
  onSaved,
}) => {
  const textos = useMemo(() => textosAgendamento(), [])
  const msg = (chave) => textoAgendamento(chave, textos)

  const [cabecalho, setCabecalho] = useState({})
  const [filtros, setFiltros] = useState({})
  const [linhas, setLinhas] = useState({})
  const [previews, setPreviews] = useState({})
  // linhas com preview em recalculo: a data anterior fica na tela, esmaecida, ate a nova chegar
  const [calculando, setCalculando] = useState({})
  const [erro, setErro] = useState(null)
  const [ocupado, setOcupado] = useState(false)
  // a listagem desmonta o dialogo ao fechar: resposta que chega depois nao mexe em estado
  const montado = useRef(true)
  // so a resposta do ultimo pedido de preview vale (uma antiga pode chegar depois da nova)
  const ultimoPreview = useRef(0)
  // a formula de cada linha cujo preview ja esta na tela: so a linha que mudou e recalculada
  const formulasResolvidas = useRef({})

  useEffect(() => {
    montado.current = true
    return () => {
      montado.current = false
    }
  }, [])

  // (re)inicia a cada abertura: novo agendamento a partir da tela, ou edicao do salvo
  useEffect(() => {
    if (!visible) {
      return
    }
    const salvo = agendamento || {}
    setCabecalho({
      descricao: salvo.descricao || (relatorio && relatorio.descricao) || '',
      assunto: salvo.assunto || '',
      email: salvo.email || '',
      cron: salvo.cron || '',
      formato: salvo.formato || 'pdf',
      ativo: salvo.ativo !== false,
    })
    setFiltros(agendamento ? { ...lerFiltros(agendamento.filtros), ...(filtrosTela || {}) } : { ...(filtrosTela || {}) })
    setLinhas(linhasIniciais(camposData, agendamento ? lerFiltros(agendamento.filtros) : null))
    setPreviews({})
    setCalculando({})
    formulasResolvidas.current = {}
    setErro(null)
  }, [visible]) // eslint-disable-line react-hooks/exhaustive-deps

  // preview das formulas marcadas, calculado pelo back (mesma regra da execucao)
  useEffect(() => {
    // todo pedido novo invalida o anterior, inclusive quando nao ha formula (resposta atrasada nao volta a tela)
    const pedido = ++ultimoPreview.current
    if (!visible) {
      return undefined
    }
    const formulas = {}
    Object.keys(linhas).forEach((campo) => {
      if (linhas[campo].usarFormula) {
        formulas[campo] = montarFormula(linhas[campo])
      }
    })
    // linha desmarcada perde o preview: marcada de novo, calcula do zero
    Object.keys(formulasResolvidas.current).forEach((campo) => {
      if (!(campo in formulas)) {
        delete formulasResolvidas.current[campo]
      }
    })
    setPreviews((atuais) => {
      const mantidos = {}
      Object.keys(atuais).forEach((campo) => {
        if (campo in formulas) {
          mantidos[campo] = atuais[campo]
        }
      })
      return Object.keys(mantidos).length === Object.keys(atuais).length ? atuais : mantidos
    })
    // so o que mudou (ou ainda nao chegou) vai ao back; as outras linhas ficam como estao
    const pendentes = {}
    Object.keys(formulas).forEach((campo) => {
      if (formulasResolvidas.current[campo] !== formulas[campo]) {
        pendentes[campo] = formulas[campo]
      }
    })
    const marcadas = {}
    Object.keys(pendentes).forEach((campo) => {
      marcadas[campo] = true
    })
    setCalculando(marcadas)
    if (Object.keys(pendentes).length === 0) {
      return undefined
    }
    const vale = () => montado.current && pedido === ultimoPreview.current
    const concluir = (resultados) => {
      setPreviews((atuais) => ({ ...atuais, ...resultados }))
      setCalculando({})
    }
    const espera = setTimeout(() => {
      resolverDatas(pendentes)
        .then((resposta) => {
          if (vale()) {
            Object.assign(formulasResolvidas.current, pendentes)
            concluir(resposta.data || {})
          }
        })
        .catch(() => {
          if (vale()) {
            // sem isso a linha ficaria esmaecida para sempre, como se ainda calculasse; a formula nao fica como
            // resolvida, entao a proxima mudanca na grade tenta de novo
            const falha = {}
            Object.keys(pendentes).forEach((campo) => {
              falha[campo] = { erro: msg('erro.preview') }
            })
            concluir(falha)
          }
        })
    }, ESPERA_PREVIEW_MS)
    return () => clearTimeout(espera)
  }, [visible, linhas]) // eslint-disable-line react-hooks/exhaustive-deps

  const opcoesValor = [
    { label: msg('formula.dataAtual'), value: '$data_atual' },
    { label: msg('formula.inicioMes'), value: '$inicio_mes' },
    { label: msg('formula.finalMes'), value: '$final_mes' },
  ]
  const opcoesPeriodo = [
    { label: msg('periodo.dia'), value: '$dia' },
    { label: msg('periodo.mes'), value: '$mes' },
    { label: msg('periodo.ano'), value: '$ano' },
  ]

  const alterarCabecalho = (campo, valor) => setCabecalho((atual) => ({ ...atual, [campo]: valor }))
  const alterarLinha = (campo, alteracao) =>
    setLinhas((atual) => ({ ...atual, [campo]: { ...atual[campo], ...alteracao } }))

  const alterarFiltro = (campo, valor) => setFiltros((atual) => ({ ...atual, [campo]: valor }))

  const montarDto = () => {
    return {
      oid: agendamento ? agendamento.oid : null,
      path: relatorio.path,
      oidDocumento: relatorio.oidDocumento || null,
      descricao: cabecalho.descricao,
      assunto: cabecalho.assunto,
      email: cabecalho.email,
      cron: cabecalho.cron,
      formato: cabecalho.formato,
      ativo: cabecalho.ativo,
      filtros: JSON.stringify(montarFiltros(filtros, camposData, linhas)),
    }
  }

  const executar = (chamada, depois, aoFalhar) => {
    setErro(null)
    setOcupado(true)
    chamada(montarDto())
      .then((resposta) => depois(resposta.data))
      .catch((e) => {
        if (aoFalhar) {
          aoFalhar()
        }
        if (montado.current) {
          setErro(mensagemDeErro(e, msg('erro.operacao')))
        }
      })
      .finally(() => montado.current && setOcupado(false))
  }

  // enquanto salva ou abre o relatorio, X e Esc nao fecham: o erro do salvamento apareceria num dialogo ja fechado
  const fechar = () => {
    if (!ocupado) {
      onHide()
    }
  }

  // a aba abre no clique: aberta so na resposta do back, o navegador a trata como pop-up e bloqueia
  const verRelatorio = () => {
    const janela = abrirAbaEmBranco()
    executar(
      visualizar,
      (url) => mostrarNaAba(janela, url),
      () => fecharAba(janela)
    )
  }

  const confirmar = () =>
    executar(salvar, (salvo) => {
      if (onSaved) {
        onSaved(salvo)
      }
      onHide()
    })

  const textoPreview = (campo) => {
    const linha = linhas[campo]
    if (!linha) {
      return ''
    }
    if (linha.usarFormula) {
      const preview = previews[campo]
      if (!preview) {
        return '...'
      }
      return preview.erro ? preview.erro : formatarDataBr(preview.data)
    }
    return null
  }

  const classePreview = (campo) => {
    const classes = ['agendar-relatorio-preview']
    if (linhas[campo] && linhas[campo].usarFormula) {
      if (previews[campo] && previews[campo].erro) {
        classes.push('agendar-relatorio-preview-erro')
      }
      if (calculando[campo]) {
        classes.push('agendar-relatorio-preview-calculando')
      }
    }
    return classes.join(' ')
  }

  // sem formula vale a data fixa (D4): a da tela ou a salva; so leitura (para trocar, informar na tela do relatorio)
  const dataFixa = (campo) => {
    const iso = isFormula(filtros[campo]) ? null : formatarDataIso(filtros[campo])
    return iso ? formatarDataBr(iso) : '-'
  }

  const footer = (
    <div>
      <Button
        label={msg('visualizar')}
        icon="pi pi-eye"
        className="p-button-secondary"
        tooltip={msg('visualizar.help')}
        onClick={verRelatorio}
        disabled={ocupado}
      />
      <Button label={msg('cancelar')} icon="pi pi-times" className="p-button-danger" onClick={fechar} disabled={ocupado} />
      <Button label={msg('agendar')} icon="pi pi-clock" className="p-button-success" onClick={confirmar} disabled={ocupado} />
    </div>
  )

  return (
    <Dialog
      header={msg('agendamentorelatorio')}
      visible={visible}
      onHide={fechar}
      closable={!ocupado}
      closeOnEscape={!ocupado}
      footer={footer}
      modal={true}
      baseZIndex={1000000}
      className="agendar-relatorio-dialog"
    >
      {erro && <Message severity="error" text={erro} className="agendar-relatorio-erro" />}

      <div className="p-grid p-fluid form-group">
        <div className="p-col-12 p-md-6">
          <label className="label">{msg('descricao')}</label>
          <LtsInputText value={cabecalho.descricao || ''} onChange={(valor) => alterarCabecalho('descricao', valor)} />
        </div>
        <div className="p-col-12 p-md-6">
          <label className="label">{msg('assunto')}</label>
          <LtsInputText value={cabecalho.assunto || ''} onChange={(valor) => alterarCabecalho('assunto', valor)} />
        </div>
        <div className="p-col-12">
          <label className="label">{msg('email')}</label>
          <LtsInputText value={cabecalho.email || ''} onChange={(valor) => alterarCabecalho('email', valor)} />
          <small>{msg('email.help')}</small>
        </div>
        <div className="p-col-12">
          <label className="label">{msg('cron')}</label>
          <LtsInputText value={cabecalho.cron || ''} onChange={(valor) => alterarCabecalho('cron', valor)} />
          <small>{msg('cron.help')}</small>
        </div>
        <div className="p-col-12">
          <Checkbox
            inputId="agendamentoRelatorioAtivo"
            checked={cabecalho.ativo}
            onChange={(e) => alterarCabecalho('ativo', e.checked)}
            style={{ marginRight: '5px' }}
          />
          <label htmlFor="agendamentoRelatorioAtivo" className="label">
            {msg('ativo')}
          </label>
        </div>
        {EditorFiltros && <EditorFiltros filtros={filtros} onChange={alterarFiltro} />}
      </div>

      {/* relatorio sem campo de data (ha telas dinamicas assim): a grade nao aparece */}
      <div className="agendar-relatorio-datas" style={camposData.length > 0 ? undefined : { display: 'none' }}>
        <label className="label">{msg('datas')}</label>
        <small>{msg('datas.help')}</small>
        <table>
          <thead>
            <tr>
              <th />
              <th>{msg('parametro')}</th>
              <th>{msg('valor')}</th>
              <th>{msg('modificador')}</th>
              <th>{msg('periodo')}</th>
              <th className="agendar-relatorio-preview">{msg('preview')}</th>
            </tr>
          </thead>
          <tbody>
            {camposData.map(({ campo, label }) => {
              const linha = linhas[campo]
              if (!linha) {
                return null
              }
              return (
                <tr key={campo}>
                  <td>
                    <Checkbox
                      checked={linha.usarFormula}
                      onChange={(e) => alterarLinha(campo, { usarFormula: e.checked })}
                    />
                  </td>
                  <td>{label}</td>
                  <td>
                    <Dropdown
                      value={linha.valor}
                      options={opcoesValor}
                      disabled={!linha.usarFormula}
                      onChange={(e) => alterarLinha(campo, { valor: e.value })}
                    />
                  </td>
                  <td>
                    <LtsInputNumber
                      value={linha.modificador}
                      showButtons
                      disabled={!linha.usarFormula}
                      inputClassName="agendar-relatorio-modificador"
                      onChange={(valor) => alterarLinha(campo, { modificador: valor === null ? 0 : valor })}
                    />
                  </td>
                  <td>
                    <Dropdown
                      value={linha.periodo}
                      options={opcoesPeriodo}
                      disabled={!linha.usarFormula}
                      onChange={(e) => alterarLinha(campo, { periodo: e.value })}
                    />
                  </td>
                  <td className={classePreview(campo)}>
                    {linha.usarFormula ? textoPreview(campo) : dataFixa(campo)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Dialog>
  )
}
